import { DataTable } from '@/components/ui/DataTable';
import { Badge } from '@/components/ui/Badge';
import { formatBytes } from '@/utils/formatters';
import { createColumnHelper } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';

interface VaultRow {
  uuid: string;
  name: string;
  entityType?: string;
  encryptionMode?: string;
  status: string;
  owner?: { uuid: string; email: string; firstName: string; lastName: string } | null;
  creator?: { uuid: string; email: string } | null;
  documentCount?: number;
  storageUsed?: number;
  [key: string]: any;
}

const columnHelper = createColumnHelper<VaultRow>();

const encryptionLabels: Record<string, string> = {
  zero_knowledge: 'Zero-Knowledge',
  server_side: 'Server-Side',
};

const typeLabels: Record<string, string> = {
  employee: 'Employé',
  folder: 'Dossier',
  company: 'Entreprise',
};

const columns = [
  columnHelper.accessor('name', {
    header: 'Coffre',
    cell: (info) => <span className="font-medium text-neutral-900 dark:text-neutral-200">{info.getValue()}</span>,
  }),
  columnHelper.display({
    id: 'owner',
    header: 'Propriétaire',
    cell: (info) => {
      const owner = info.row.original.owner;
      return owner ? (
        <span className="text-xs text-neutral-600 dark:text-neutral-400">{owner.firstName} {owner.lastName}</span>
      ) : (
        <span className="text-xs text-neutral-400">-</span>
      );
    },
  }),
  columnHelper.accessor('entityType', {
    header: 'Type',
    cell: (info) => {
      const val = info.getValue();
      return val ? <Badge variant="info">{typeLabels[val] || val}</Badge> : <span className="text-neutral-400">-</span>;
    },
  }),
  columnHelper.accessor('encryptionMode', {
    header: 'Mode',
    cell: (info) => {
      const val = info.getValue();
      return val ? (
        <Badge variant={val === 'zero_knowledge' ? 'warning' : 'neutral'}>
          {encryptionLabels[val] || val}
        </Badge>
      ) : <span className="text-neutral-400">-</span>;
    },
  }),
  columnHelper.accessor('status', {
    header: 'Statut',
    cell: (info) => {
      const val = info.getValue();
      const variant = val === 'active' ? 'success' : val === 'pending_user_activation' ? 'warning' : 'neutral';
      return <Badge variant={variant}>{val}</Badge>;
    },
  }),
  columnHelper.accessor('documentCount', {
    header: 'Documents',
    cell: (info) => <span className="text-neutral-600 dark:text-neutral-400">{info.getValue() ?? '-'}</span>,
  }),
  columnHelper.accessor('storageUsed', {
    header: 'Stockage',
    cell: (info) => <span className="text-xs text-neutral-500">{formatBytes(info.getValue())}</span>,
  }),
];

interface VaultsTableProps {
  data: VaultRow[];
  loading?: boolean;
}

export function VaultsTable({ data, loading }: VaultsTableProps) {
  const navigate = useNavigate();
  return (
    <DataTable columns={columns} data={data} loading={loading} onRowClick={(row) => navigate(`/vaults/${row.uuid}`)} emptyMessage="Aucun coffre trouvé" />
  );
}
